import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageCode } from '../amorg-common';
import { LoadingPageOverlay } from '../designSystem/LoadingPageOverlay';
import { SetQuestionSelection } from '../features/surveyAnswers/SetQuestionSelection';
import { stxt } from '../SurveyTxt';
import styled from 'styled-components';
import { SetSegments } from '../features/surveyAnswers/SetSegments';
import { Card } from '../designSystem/Card';
import { MessageCard } from '../designSystem/Message';
import { SingleColumnLayout } from '../designSystem/layouts/SingleColumnLayout';
import { NoNavigationPageWrapper } from '../designSystem/layouts/pageWrappers/NoNavigationPageWrapper';
import { getAnswerStatus, submitSegments, submitSelection } from '../api/surveySubmissionClientApi';
import { SurveyAnswerStatus } from '../amorg-common';
import { SurveySegment } from '../amorg-common';
import { SurveyDoneView } from '../features/surveyAnswers/SurveyDone';
import { SurveyIntroductionView } from '../features/surveyAnswers/SurveyIntroduction';
import { LanguageSelector } from '../designSystem/compositeComponents/LanguageSelector';
import { AnswerView, answerStateMachine } from '../utils/answerStateMachine';
import { PageHeader } from '../designSystem/PageHeader';
import { PublicPageProvider } from './_contexts/PublicPageContext';

type PageState = 
{
    answerStatus:SurveyAnswerStatus,
    display:AnswerView
};


export function SurveyAnswerPage(): JSX.Element {
    const params = useParams<{ siteId: string, surveyId: string, answerId: string }>();
    const [availableLanguages, setAvailableLanguages] = useState<LanguageCode[]>([]);
    const [selectedLanguage,setSelectedLanguage] = useState<LanguageCode | null>(null);
    const [state, setState] = useState<PageState|null>(null);

    // Setting initial state
    useEffect(() => {
        const getData = async () => {
            const answerStatus = await getAnswerStatus(params.siteId, params.surveyId, params.answerId);
            if(answerStatus.status === 'ok')
            {
                setAvailableLanguages(answerStatus.languages);
                setSelectedLanguage(answerStatus.defaultLanguage);
            }
            setState({
                display: answerStateMachine(answerStatus).initializeView(),
                answerStatus
            });
        };
        getData();
    }, []);

    async function handleIntrodutionClickNext() {
        const answerStatus = await getAnswerStatus(params.siteId, params.surveyId, params.answerId);
        setState({
            display: answerStateMachine(answerStatus).clickIntroductionNext(),
            answerStatus
        });
    } 

    async function handleSubmitSegments(segments: SurveySegment[]) {
        await submitSegments(
            params.siteId,
            params.surveyId,
            params.answerId,
            segments
        );

        const answerStatus = await getAnswerStatus(params.siteId, params.surveyId, params.answerId);
        setState({
            display: answerStateMachine(answerStatus).submittedSegments(),
            answerStatus
        });
    }

    async function handleSubmitSelection(value: number, comment: string) {
        if (state.display.name === 'question') {
            await submitSelection(
                params.siteId,
                params.surveyId,
                params.answerId,
                state.display.questionIndex,
                value,
                comment
            );
        }
        const answerStatus = await getAnswerStatus(params.siteId, params.surveyId, params.answerId);
        setState({
            display: answerStateMachine(answerStatus).questionSubmitted(),
            answerStatus
        })
    }

    async function handlePreviousQuestionClicked() {
        setState({
            display: answerStateMachine(state.answerStatus).clickPrevious(state.display),
            answerStatus: state.answerStatus
        })
    }

    const txt = stxt(selectedLanguage);

    let view = <LoadingPageOverlay loading={true} />;

    if(state){
        if(state.display.name === 'unavailable')
            view = <Card variant="outlined">
                <MessageCard variant="error">
                    {txt.overview_surveyClosed}
                </MessageCard>
            </Card>;

        else if (state.display.name === 'introduction')
            view = 
            <SurveyIntroductionView
                txt={txt}
                selectedLanguage={selectedLanguage}
                answerStatus={state.display.answerStatus}
                onClickStart={handleIntrodutionClickNext}
            />;

        else if (state.display.name === 'segmentation')
            view =
                <SetSegments
                    txt={txt}
                    selectedLanguage={selectedLanguage}
                    answerStatus={state.display.answerStatus}
                    onSubmitSegments={handleSubmitSegments} />

        else if (state.display.name === 'question')
            view =
                <SetQuestionSelection
                txt={txt}
                    selectedLanguage={selectedLanguage}
                    answerStatus={state.display.answerStatus}
                    questionIndex={state.display.questionIndex}
                    onPreviousClicked={handlePreviousQuestionClicked}
                    onSubmitSelection={handleSubmitSelection} />;

        else if (state.display.name === 'done')
            view = <SurveyDoneView txt={txt} />;
    }

    return (
        <PublicPageProvider>
        <NoNavigationPageWrapper>
            <SingleColumnLayout
                noDivider
                contentContainer={
                    <>
                    <PageHeader headerText={txt.answerPage_header} headerStyle='primary-black'
                        buttons={
                            <>
                                {availableLanguages.length > 1 && selectedLanguage &&
                                    <LanguageSelector
                                        availableLanguages={availableLanguages}
                                        selectedLanguage={selectedLanguage}
                                        onSelectedLanguageChange={(languageCode) => setSelectedLanguage(languageCode)} />
                                }
                            </>
                        }/>
                    <QuestionWrapper>
                        {view}
                    </QuestionWrapper>
                    </>
                }
            />
        </NoNavigationPageWrapper>
        </PublicPageProvider>
    );
}

const QuestionWrapper = styled.div`
    display: grid;
    justify-self:center;
    width: 100%;
`;